import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Building2, Mail, Phone, Globe, MapPin, Clock, Tag, Plus, X, Store } from "lucide-react";
import LogoUpload from "@/components/LogoUpload";
import { HoursSelector } from "@/components/HoursSelector";
import { normalizeSocialUrl, normalizeUrl } from "@/lib/urlUtils";
import { AddressFields } from "@/components/AddressFields";

const BUSINESS_TYPE_OPTIONS = [
  "Goods", "Services", "Food & Beverage", "Nonprofits",
];
const BUSINESS_SUBCATEGORY_OPTIONS = {
  Goods: ["Art", "Books", "Candles", "Clothing", "Home Goods", "Jewelry", "Plants", "Skincare", "Vintage"],
  Services: ["Beauty", "Creative Services", "Education", "Event Services", "Fitness & Wellness", "Home Services", "Pet Services", "Photography", "Professional Services", "Repair Services"],
  "Food & Beverage": ["Bakery", "Beverages", "Catering", "Coffee & Tea", "Desserts", "Food Trucks", "Prepared Foods", "Specialty Foods", "Snacks"],
  Nonprofits: ["Chambers of Commerce"],
};
const LOCATION_OPTIONS = [
  "Clintonville", "Columbus", "East Market", "German Village", "Hilliard",
  "Linden", "New Albany", "Reynoldsburg", "Short North", "Westerville", "Worthington",
  "Outside of the Greater Columbus Area",
];

/**
 * Shared bento-grid editor for a Business profile.
 * Used by /dashboard (owner) and /admin/business/:email (admin).
 *
 * Props:
 *   data        — business object
 *   onChange    — (field, value) => void
 *   token       — bearer token for logo uploads
 *   uploadEndpoint — '/api/upload/logo' (owner) or '/api/admin/upload/logo' (admin)
 *   emailReadOnly — bool, default true (email cannot be changed inline)
 *   prefixSlot  — optional ReactNode rendered before the grid (e.g. SubscriptionCard)
 *   suffixSlot  — optional ReactNode rendered after Social card (e.g. MyEvents, AdminControls)
 */
export const BusinessProfileForm = ({
  data,
  onChange,
  token,
  uploadEndpoint = "/api/upload/logo",
  emailReadOnly = true,
  addressErrorIndexes = [],
  marketOptions = [],
  prefixSlot = null,
  suffixSlot = null,
}) => {
  const setField = (field, value) => onChange(field, value);
  const selectedCategories = data.business_categories || [];
  const subcategoryOptions = data.business_type ? BUSINESS_SUBCATEGORY_OPTIONS[data.business_type] || [] : [];
  const selectedLocations = Array.isArray(data.business_locations)
    ? data.business_locations
    : data.location
      ? [data.location]
      : [];
  const addresses = Array.isArray(data.business_addresses) ? data.business_addresses : data.address ? [data.address] : [];
  const additionalLocations = Array.isArray(data.additional_business_locations) ? data.additional_business_locations : [];
  const canAddAdditionalLocations = !data.is_home_based && selectedLocations.some((location) => location.toLowerCase() === "columbus");
  const vendorRequests = Array.isArray(data.market_vendor_requests) ? data.market_vendor_requests : [];
  const selectedVendorMarketIds = Array.isArray(data.farmers_market_vendor_market_ids)
    ? data.farmers_market_vendor_market_ids
    : vendorRequests.map((request) => request.market_id).filter(Boolean);
  const [customLocation, setCustomLocation] = useState("");

  const handleBusinessTypeChange = (value) => {
    setField("business_type", value);
    setField("business_categories", []);
    setField("business_category_other", "");
  };

  const toggleBusinessCategory = (category) => {
    const next = selectedCategories.includes(category)
      ? selectedCategories.filter((item) => item !== category)
      : [...selectedCategories, category];
    setField("business_categories", next);
    if (!next.includes("Other")) setField("business_category_other", "");
  };

  const setBusinessLocations = (nextLocations) => {
    const cleanedLocations = Array.from(new Set(nextLocations.map((item) => item.trim()).filter(Boolean)));
    const cleaned = data.is_home_based ? cleanedLocations.slice(-1) : cleanedLocations;
    const nextAddresses = cleaned.map((location) => {
      const previousIndex = selectedLocations.indexOf(location);
      return previousIndex >= 0 ? addresses[previousIndex] || "" : "";
    });
    setField("business_locations", cleaned);
    setField("location", cleaned[0] || "");
    setField("business_addresses", nextAddresses);
    setField("address", nextAddresses.map((item) => item.trim()).filter(Boolean).join("\n\n"));
    if (!cleaned.some((location) => location.toLowerCase() === "columbus")) {
      setField("additional_business_locations", []);
    }
  };

  const toggleLocation = (location) => {
    if (data.is_home_based && !selectedLocations.includes(location)) {
      setBusinessLocations([location]);
      return;
    }
    const next = selectedLocations.includes(location)
      ? selectedLocations.filter((item) => item !== location)
      : [...selectedLocations, location];
    setBusinessLocations(next);
  };

  const addCustomLocation = () => {
    if (!customLocation.trim()) return;
    setBusinessLocations(data.is_home_based ? [customLocation] : [...selectedLocations, customLocation]);
    setCustomLocation("");
  };

  const handleHomeBasedChange = (checked) => {
    const isHomeBased = checked === true;
    setField("is_home_based", isHomeBased);
    if (isHomeBased) {
      if (selectedLocations.length > 1) {
        const primaryLocation = selectedLocations[0];
        setField("business_locations", primaryLocation ? [primaryLocation] : []);
        setField("location", primaryLocation || "");
      }
      setField("business_addresses", []);
      setField("address", "");
      setField("additional_business_locations", []);
    }
  };

  const setBusinessAddresses = (nextAddresses) => {
    setField("business_addresses", nextAddresses);
    setField("address", nextAddresses.map((item) => item.trim()).filter(Boolean).join("\n\n"));
  };

  const updateAddress = (index, value) => {
    const next = selectedLocations.map((_, itemIndex) => itemIndex === index ? value : addresses[itemIndex] || "");
    setBusinessAddresses(next);
  };

  const addAdditionalLocation = () => {
    setField("additional_business_locations", [...additionalLocations, { name: "", address: "" }]);
  };

  const updateAdditionalLocation = (index, field, value) => {
    const nextLocations = additionalLocations.map((location, itemIndex) => (
      itemIndex === index ? { ...location, [field]: value } : location
    ));
    setField("additional_business_locations", nextLocations);
  };

  const removeAdditionalLocation = (index) => {
    setField("additional_business_locations", additionalLocations.filter((_, itemIndex) => itemIndex !== index));
  };

  const toggleFarmersMarketVendor = (checked) => {
    const enabled = checked === true;
    setField("is_farmers_market_vendor", enabled);
    if (!enabled) {
      setField("farmers_market_vendor_market_ids", []);
      setField("market_vendor_requests", []);
    }
  };

  const toggleVendorMarket = (marketId) => {
    const nextIds = selectedVendorMarketIds.includes(marketId)
      ? selectedVendorMarketIds.filter((id) => id !== marketId)
      : [...selectedVendorMarketIds, marketId];
    setField("farmers_market_vendor_market_ids", nextIds);
    setField("is_farmers_market_vendor", nextIds.length > 0);
  };

  const vendorStatusForMarket = (marketId) => {
    const request = vendorRequests.find((item) => item.market_id === marketId);
    return request?.status || "pending";
  };

  const vendorStatusBadgeClass = (status) => {
    if (status === "approved") return "bg-emerald-100 text-emerald-800";
    if (status === "rejected") return "bg-red-100 text-red-800";
    return "bg-amber-100 text-amber-800";
  };

  const vendorStatusLabel = (status) => {
    if (status === "approved") return "Verified";
    if (status === "rejected") return "Not Approved";
    return "Pending";
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
      {prefixSlot}

      {/* Identity Card */}
      <Card className="col-span-1 md:col-span-8 bg-white rounded-xl border border-slate-100 shadow-sm card-hover" data-testid="identity-card">
        <CardHeader className="pb-4">
          <CardTitle className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-slate-400" /> Business Identity
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-shrink-0 w-full md:w-auto">
              <Label className="text-slate-700 font-medium mb-2 block">Logo</Label>
              <LogoUpload
                currentLogo={data.logo}
                businessName={data.business_name}
                onUploadSuccess={(logoUrl) => {
                  setField("logo", logoUrl);
                  if (!uploadEndpoint.includes("/admin/")) setField("profile_review_needed", true);
                }}
                uploadEndpoint={uploadEndpoint}
                token={token}
              />
            </div>
            <div className="flex-1 space-y-4">
              <div>
                <Label className="text-slate-700 font-medium">Business Name</Label>
                <Input
                  value={data.business_name || ""}
                  onChange={(e) => setField("business_name", e.target.value)}
                  placeholder="Your Business Name"
                  className="h-12 mt-1 border-slate-200"
                  data-testid="business-name-input"
                />
              </div>
              <div>
                <Label className="text-slate-700 font-medium">Description</Label>
                <Textarea
                  value={data.business_description || ""}
                  onChange={(e) => setField("business_description", e.target.value)}
                  placeholder="Tell customers about your business..."
                  className="mt-1 min-h-[100px] border-slate-200 resize-y"
                  data-testid="description-input"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Type Card */}
      <Card className="col-span-1 md:col-span-4 bg-white rounded-xl border border-slate-100 shadow-sm card-hover" data-testid="meta-card">
        <CardHeader className="pb-4">
          <CardTitle className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Tag className="w-5 h-5 text-slate-400" /> Business Categories
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-slate-700 font-medium">Main Category</Label>
            <Select value={data.business_type || ""} onValueChange={handleBusinessTypeChange}>
              <SelectTrigger className="h-12 mt-1 border-slate-200" data-testid="business-type-select"><SelectValue placeholder="Choose a main category" /></SelectTrigger>
              <SelectContent>
                {BUSINESS_TYPE_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-700 font-medium">Subcategories</Label>
            <div className="mt-2 rounded-xl border border-slate-200 p-3">
              {!data.business_type ? (
                <p className="text-sm text-slate-400">Choose a main category first.</p>
              ) : (
                <div className="space-y-2">
                  {[...subcategoryOptions, "Other"].map((category) => (
                    <label key={category} className="flex items-center gap-2 text-sm text-slate-600">
                      <Checkbox
                        checked={selectedCategories.includes(category)}
                        onCheckedChange={() => toggleBusinessCategory(category)}
                        data-testid={`business-subcategory-${category.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      />
                      {category}
                    </label>
                  ))}
                </div>
              )}
              {selectedCategories.includes("Other") && (
                <Input
                  value={data.business_category_other || ""}
                  onChange={(e) => setField("business_category_other", e.target.value)}
                  placeholder="Add another category"
                  className="h-11 mt-3 border-slate-200"
                  data-testid="business-subcategory-other"
                />
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact */}
      <Card className="col-span-1 md:col-span-4 bg-white rounded-xl border border-slate-100 shadow-sm card-hover" data-testid="contact-card">
        <CardHeader className="pb-4">
          <CardTitle className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Mail className="w-5 h-5 text-slate-400" /> Contact Info
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-slate-700 font-medium"><Mail className="w-4 h-4 inline mr-1" /> Email</Label>
            <Input
              value={data.business_email || ""}
              onChange={(e) => !emailReadOnly && setField("business_email", e.target.value)}
              disabled={emailReadOnly}
              className={`h-12 mt-1 border-slate-200 ${emailReadOnly ? "bg-slate-50 text-slate-500" : ""}`}
              data-testid="email-display"
            />
          </div>
          <div>
            <Label className="text-slate-700 font-medium"><Phone className="w-4 h-4 inline mr-1" /> Phone</Label>
            <Input
              value={data.phone_number || ""}
              onChange={(e) => setField("phone_number", e.target.value)}
              placeholder="+1 (555) 123-4567"
              className="h-12 mt-1 border-slate-200"
              data-testid="phone-input"
            />
          </div>
          <div>
            <Label className="text-slate-700 font-medium"><Globe className="w-4 h-4 inline mr-1" /> Website</Label>
            <Input
              type="url"
              value={data.website || ""}
              onChange={(e) => setField("website", e.target.value)}
              onBlur={(e) => setField("website", normalizeUrl(e.target.value))}
              placeholder="https://yourbusiness.com"
              className="h-12 mt-1 border-slate-200"
              data-testid="website-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Location */}
      <Card className="col-span-1 md:col-span-4 bg-white rounded-xl border border-slate-100 shadow-sm card-hover" data-testid="location-card">
        <CardHeader className="pb-4">
          <CardTitle className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-slate-400" /> Location
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-slate-700 font-medium">City/Region Tags</Label>
            {data.is_home_based && (
              <p className="mt-1 text-xs text-slate-500">Home-based businesses can choose one city/region.</p>
            )}
            <div className="mt-2 rounded-xl border border-slate-200 p-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {LOCATION_OPTIONS.map((location) => (
                  <label key={location} className="flex items-center gap-2 text-sm text-slate-600">
                    <Checkbox
                      checked={selectedLocations.includes(location)}
                      onCheckedChange={() => toggleLocation(location)}
                      data-testid={`business-location-${location.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    />
                    {location}
                  </label>
                ))}
              </div>
              {selectedLocations.filter((location) => !LOCATION_OPTIONS.includes(location)).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {selectedLocations.filter((location) => !LOCATION_OPTIONS.includes(location)).map((location) => (
                    <span key={location} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                      {location}
                      <button type="button" onClick={() => toggleLocation(location)} aria-label={`Remove ${location}`}>
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-3 flex gap-2">
                <Input
                  value={customLocation}
                  onChange={(e) => setCustomLocation(e.target.value)}
                  placeholder="Add another city or region"
                  className="h-11 border-slate-200"
                  data-testid="location-other-input"
                />
                <Button type="button" variant="outline" onClick={addCustomLocation} className="h-11 shrink-0 rounded-full" data-testid="add-location-btn">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
          <div>
            <label className="flex items-start gap-3 rounded-xl bg-slate-50 p-3 text-sm text-slate-600">
              <Checkbox
                checked={!!data.is_home_based}
                onCheckedChange={handleHomeBasedChange}
                data-testid="home-based-input"
              />
              <span>
                <span className="font-medium text-slate-800">I am a home-based business.</span>{" "}
                Do not show my physical address in the public directory.
              </span>
            </label>
            {!data.is_home_based && (
              <div className="mt-3">
                <Label className="text-slate-700 font-medium">Addresses by City/Region</Label>
                {selectedLocations.length === 0 ? (
                  <p className="mt-2 rounded-xl border border-dashed border-slate-200 p-3 text-sm text-slate-400">
                    Select at least one city/region tag to add an address.
                  </p>
                ) : (
                  <div className="mt-2 space-y-3">
                    {selectedLocations.map((location, index) => (
                      <div
                        key={location}
                        className={`rounded-xl border p-3 ${addressErrorIndexes.includes(index) ? "border-red-300 bg-red-50/60" : "border-slate-200"}`}
                      >
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <p className="text-xs font-medium uppercase tracking-wider text-slate-400">{location}</p>
                      </div>
                      {addressErrorIndexes.includes(index) && (
                        <p className="mb-2 rounded-lg bg-white/80 px-3 py-2 text-sm font-medium text-red-700">
                          Please enter a full address with street, city, state, and ZIP.
                        </p>
                      )}
                      <AddressFields
                        value={addresses[index] || ""}
                        onChange={(value) => updateAddress(index, value)}
                        label=""
                        cityHint={location}
                        testIdPrefix={`address-input-${index}`}
                        error={addressErrorIndexes.includes(index)}
                      />
                    </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {canAddAdditionalLocations && (
              <div className="mt-4 rounded-xl border border-dashed border-[#42D78A]/50 bg-[#42D78A]/5 p-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <Label className="text-slate-700 font-medium">Additional Columbus Locations</Label>
                    <p className="mt-1 text-xs text-slate-500">Add another address and a public name for that location.</p>
                  </div>
                  <Button type="button" variant="outline" onClick={addAdditionalLocation} className="h-10 shrink-0 rounded-full" data-testid="add-additional-location-btn">
                    <Plus className="mr-2 h-4 w-4" /> Add Location
                  </Button>
                </div>
                {additionalLocations.length > 0 && (
                  <div className="mt-3 space-y-3">
                    {additionalLocations.map((location, index) => (
                      <div key={index} className="rounded-xl border border-slate-200 bg-white p-3">
                        <div className="mb-3 flex items-center gap-2">
                          <div className="min-w-0 flex-1">
                            <Label className="text-slate-700 font-medium">Location Name</Label>
                            <Input
                              value={location.name || ""}
                              onChange={(event) => updateAdditionalLocation(index, "name", event.target.value)}
                              placeholder="Example: Studio, Pickup Window, Second Shop"
                              className="mt-1 h-11 border-slate-200"
                              data-testid={`additional-location-name-${index}`}
                            />
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeAdditionalLocation(index)}
                            className="mt-6 h-10 w-10 shrink-0 rounded-full text-slate-400 hover:text-red-600"
                            aria-label="Remove additional location"
                            data-testid={`remove-additional-location-${index}`}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        <AddressFields
                          value={location.address || ""}
                          onChange={(value) => updateAdditionalLocation(index, "address", value)}
                          label="Additional Address"
                          cityHint="Columbus"
                          testIdPrefix={`additional-address-${index}`}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Hours */}
      <Card className="col-span-1 md:col-span-4 bg-white rounded-xl border border-slate-100 shadow-sm card-hover" data-testid="hours-card">
        <CardHeader className="pb-4">
          <CardTitle className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Clock className="w-5 h-5 text-slate-400" /> Operating Hours
          </CardTitle>
        </CardHeader>
        <CardContent>
          <HoursSelector value={data.hours} onChange={(schedule) => setField("hours", schedule)} />
        </CardContent>
      </Card>

      {/* Market Vendor */}
      <Card className="col-span-1 md:col-span-12 bg-white rounded-xl border border-slate-100 shadow-sm card-hover" data-testid="farmers-market-vendor-card">
        <CardHeader className="pb-4">
          <CardTitle className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Store className="w-5 h-5 text-slate-400" /> Market Vendor
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-start gap-3 rounded-xl bg-slate-50 p-3 text-sm text-slate-600">
            <Checkbox
              checked={!!data.is_farmers_market_vendor}
              onCheckedChange={toggleFarmersMarketVendor}
              data-testid="farmers-market-vendor-input"
            />
            <span>
              <span className="font-medium text-slate-800">I am a market vendor.</span>{" "}
              Select the markets you are part of so they can verify your vendor status.
            </span>
          </label>

          {!!data.is_farmers_market_vendor && (
            <div className="rounded-xl border border-slate-200 p-3">
              {marketOptions.length === 0 ? (
                <p className="text-sm text-slate-500">No approved markets are available yet.</p>
              ) : (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {marketOptions.map((market) => {
                    const selected = selectedVendorMarketIds.includes(market.id);
                    const status = vendorStatusForMarket(market.id);
                    return (
                      <label key={market.id} className="flex items-start gap-2 rounded-lg p-2 text-sm text-slate-600 hover:bg-slate-50">
                        <Checkbox
                          checked={selected}
                          onCheckedChange={() => toggleVendorMarket(market.id)}
                          data-testid={`vendor-market-${market.id}`}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block font-medium text-slate-800">{market.market_name || "Unnamed Market"}</span>
                          {selected && (
                            <Badge className={`mt-1 ${vendorStatusBadgeClass(status)}`}>
                              {vendorStatusLabel(status)}
                            </Badge>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Social */}
      <Card className="col-span-1 md:col-span-12 bg-white rounded-xl border border-slate-100 shadow-sm card-hover" data-testid="social-card">
        <CardHeader className="pb-4">
          <CardTitle className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Globe className="w-5 h-5 text-slate-400" /> Social Media
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-slate-700 font-medium flex items-center gap-2">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                Instagram
              </Label>
              <Input
                value={data.instagram || ""}
                onChange={(e) => setField("instagram", e.target.value)}
                onBlur={(e) => setField("instagram", normalizeSocialUrl(e.target.value))}
                placeholder="https://instagram.com/yourbusiness"
                className="h-12 mt-1 border-slate-200"
                data-testid="instagram-input"
              />
            </div>
            <div>
              <Label className="text-slate-700 font-medium flex items-center gap-2">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                Facebook
              </Label>
              <Input
                value={data.facebook || ""}
                onChange={(e) => setField("facebook", e.target.value)}
                onBlur={(e) => setField("facebook", normalizeSocialUrl(e.target.value))}
                placeholder="https://facebook.com/yourbusiness"
                className="h-12 mt-1 border-slate-200"
                data-testid="facebook-input"
              />
            </div>
            <div>
              <Label className="text-slate-700 font-medium flex items-center gap-2">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64c.3 0 .59.05.86.13V9.4a6.35 6.35 0 00-.86-.06A6.33 6.33 0 003.15 15.67a6.33 6.33 0 0010.82 4.48 6.33 6.33 0 001.86-4.48V8.73a8.28 8.28 0 004.84 1.55V6.83c-.37 0-.73-.05-1.08-.14z"/></svg>
                TikTok
              </Label>
              <Input
                value={data.tiktok || ""}
                onChange={(e) => setField("tiktok", e.target.value)}
                onBlur={(e) => setField("tiktok", normalizeSocialUrl(e.target.value))}
                placeholder="https://tiktok.com/@yourbusiness"
                className="h-12 mt-1 border-slate-200"
                data-testid="tiktok-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {suffixSlot}
    </div>
  );
};
